import React, { useState, useContext, useEffect, useRef } from 'react';
import { doc, updateDoc, getDoc, collection, getDocs, query, setDoc, where, orderBy, limit, deleteDoc, writeBatch, addDoc, increment } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faPlus, 
  faTimes, 
  faTrash,
  faUserPlus,
  faSpinner,
  faUsers,
  faEdit,
  faLock,
  faUser,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { liteClient } from 'algoliasearch/lite';
import { UserContext } from '../../context/UserContext';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { createNotification, NOTIFICATION_TYPES } from '../../utils/NotificationService';
import verifiedBadge from '../../assets/Images/profileverifiedtick.png';
import defaultUserImage from '../../assets/Images/defaultUserProfilePicture.png';
import LeftMenu from '../LeftMenu';
import RightMenu from '../RightMenu';
import styles from './OrganizationManagers.module.css'; // Reusing the same styles for consistency
import AuthCheckWrapper from '../AuthCheckWrapper';

// Import useNavigate for profile navigation
import { useNavigate } from 'react-router-dom';

// Initialize Algolia
const client = liteClient("6DJNAZSX4P", "342932cbe26f7751ec87fa2b1ff32e4c");

// Permission Denied Component
const PermissionDenied = () => (
  <div className={styles.permissionDenied}>
    <FontAwesomeIcon icon={faLock} className={styles.lockIcon} />
    <h2>Access Restricted</h2>
    <p>You don't have permission to view this page. This section is only available to Organization accounts.</p>
  </div>
);

const OrganizationManagers = () => {
  const [isManagerModalVisible, setIsManagerModalVisible] = useState(false);
  const [managers, setManagers] = useState([]);
  const [displayedManagers, setDisplayedManagers] = useState([]);
  const [hasMoreManagers, setHasMoreManagers] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [managerCount, setManagerCount] = useState(0); // Total count for managers
  const { userData } = useContext(UserContext);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [tours, setTours] = useState([]); // For RightMenu
  // Add navigate hook for routing
  const navigate = useNavigate();
  const [hasAccess, setHasAccess] = useState(false);
  
  // Manager search states
  const [managerSearchQuery, setManagerSearchQuery] = useState('');
  const [managerResults, setManagerResults] = useState([]);
  const [searchingManager, setSearchingManager] = useState(false);

  // Check if user has access based on userType
  useEffect(() => {
    if (userData) {
      setHasAccess(userData.userType === 'Organization');
    }
  }, [userData]);

  // Check for screen size changes
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Load manager details with subcollection pattern
  useEffect(() => {
    if (!userData?.userId) return;
    
    const loadManagerData = async () => {
      setIsProcessing(true);
      try {
        const db = getFirestoreInstance();
        
        // First get the user document to get the manager count and top 3 managers
        const userDoc = await getDoc(doc(db, 'users', userData.userId));
        let initialManagers = [];
        let totalManagerCount = 0;
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          initialManagers = userData.organizationManagers || [];
          totalManagerCount = userData.organizationManagersCount || 0;
          
          // Set the count state
          setManagerCount(totalManagerCount);
        }
        
        // Set the initial displayed managers
        setDisplayedManagers(initialManagers);
        
        // Check if there are more managers than what's displayed
        setHasMoreManagers(totalManagerCount > initialManagers.length);
        
        // If we need to display managers, fetch them from subcollection
        if (initialManagers.length > 0) {
          // Fetch all managers from subcollection for complete data
          const managersRef = collection(db, 'users', userData.userId, 'organizationManagers');
          const managersQuery = query(
            managersRef,
            orderBy('createdAt', 'desc')
          );
          
          const managersSnapshot = await getDocs(managersQuery);
          
          if (!managersSnapshot.empty) {
            const allManagers = await Promise.all(
              managersSnapshot.docs.map(async (docSnapshot) => {
                const managerData = docSnapshot.data();
                
                // Enrich with user data
                if (managerData.userId) {
                  try {
                    const userDoc = await getDoc(doc(db, 'users', managerData.userId));
                    if (userDoc.exists()) {
                      const userData = userDoc.data();
                      return {
                        id: docSnapshot.id,
                        userId: managerData.userId,
                        fullName: userData.fullName || '',
                        userName: userData.userName || '',
                        profilePictureUrl: userData.profilePictureUrl || '',
                        userSubType: userData.userSubType || '',
                        email: userData.email || '',
                        idVerificationStatus: userData.idVerificationStatus || '',
                        status: managerData.status || 'pending',
                        role: managerData.role || 'manager',
                        createdAt: managerData.createdAt || new Date().toISOString()
                      };
                    }
                  } catch (error) {
                    console.error(`Error fetching user data for ${managerData.userId}:`, error);
                  }
                }
                
                // Return the manager data as is if user data enrichment failed
                return {
                  id: docSnapshot.id,
                  ...managerData
                };
              })
            );
            
            // Set the full managers list
            setManagers(allManagers);
          } else {
            setManagers(initialManagers);
          }
        }
      } catch (error) {
        console.error('Error fetching manager data:', error);
      } finally {
        setIsProcessing(false);
      }
    };
    
    loadManagerData();
  }, [userData?.userId]);

  // Add function to navigate to manager profile
  const navigateToUserProfile = (userName) => {
    navigate(`/${userName}`);
  };

  // Load more managers from subcollection
  const loadMoreManagers = async () => {
    if (isLoadingMore || !userData?.userId) return;
    
    setIsLoadingMore(true);
    try {
      // Skip loading more if we already have all managers loaded
      if (displayedManagers.length >= managerCount) {
        setHasMoreManagers(false);
        return;
      }
      
      // Load the next batch of managers
      const nextBatch = managers.slice(
        displayedManagers.length,
        displayedManagers.length + 6
      );
      
      setDisplayedManagers(prev => [...prev, ...nextBatch]);
      
      // Check if there are more managers to load
      setHasMoreManagers(displayedManagers.length + nextBatch.length < managerCount);
    } catch (error) {
      console.error('Error loading more managers:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleManagerSearch = async () => {
    if (!managerSearchQuery.trim()) return;
    
    try {
      setSearchingManager(true);
      const { results } = await client.search([{
        indexName: 'users_index',
        query: managerSearchQuery,
        filters: 'userType:Manager', // Search for Manager users
        hitsPerPage: 10
      }]);
  
      const formattedManagers = results[0].hits.map(hit => ({
        id: hit.objectID,
        fullName: hit.fullName,
        email: hit.email,
        userName: hit.userName,
        userSubType: hit.userSubType,
        profilePictureUrl: hit.profilePictureUrl,
        userType: hit.userType,
        idVerificationStatus: hit.idVerificationStatus,
        isAlreadyAdded: managers.some(manager => manager.userId === hit.objectID)
      }));
      
      setManagerResults(formattedManagers);
    } catch (error) {
      console.error('Error searching managers:', error);
      setManagerResults([]);
    } finally {
      setSearchingManager(false);
    }
  };

  const handleAddManager = async (manager) => {
    if (!manager || manager.isAlreadyAdded) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Prepare manager data
      const managerData = {
        userId: manager.id,
        status: 'pending',
        role: 'manager',
        addedAt: new Date().toISOString(),
        createdAt: new Date().toISOString()
      };
      
      // Add to subcollection
      const managersRef = collection(db, 'users', userData.userId, 'organizationManagers');
      const newManagerRef = await addDoc(managersRef, managerData);
      
      // Get enriched manager data for UI
      const enrichedManagerData = {
        id: newManagerRef.id,
        userId: manager.id,
        fullName: manager.fullName || '',
        userName: manager.userName || '',
        profilePictureUrl: manager.profilePictureUrl || '',
        userSubType: manager.userSubType || '',
        email: manager.email || '',
        idVerificationStatus: manager.idVerificationStatus || '',
        status: 'pending',
        role: 'manager',
        createdAt: new Date().toISOString()
      };
      
      // Update both state arrays
      setManagers(prev => [enrichedManagerData, ...prev]);
      
      // Increment the manager count
      const newManagerCount = managerCount + 1;
      setManagerCount(newManagerCount);
      
      // Update the top 3 managers in the main document
      const top3Query = query(
        managersRef,
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3Snapshot = await getDocs(top3Query);
      const top3Managers = await Promise.all(
        top3Snapshot.docs.map(async (docSnapshot) => {
          const managerData = docSnapshot.data();
          
          // Enrich with user data
          if (managerData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', managerData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  userId: managerData.userId,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  status: managerData.status || 'pending',
                  role: managerData.role || 'manager',
                  createdAt: managerData.createdAt
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${managerData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...managerData
          };
        })
      );
      
      // Update main document with top3 and the count
      const userRef = doc(db, 'users', userData.userId);
      await updateDoc(userRef, {
        organizationManagers: top3Managers,
        organizationManagersCount: newManagerCount
      });
      
      // Update displayed managers
      setDisplayedManagers(top3Managers);
      
      // Update hasMoreManagers based on the new count
      setHasMoreManagers(newManagerCount > top3Managers.length);
      
      // Create manager invitation notification
      const invitationData = {
        organizationId: userData?.userId || null,
        organizationName: userData?.fullName || '',
        organizationType: userData?.userSubType || 'Vendor',
        userId: manager.id || null,
        userName: manager.userName || '',
        fullName: manager.fullName || '',
        email: manager.email || '',
        profilePictureUrl: manager.profilePictureUrl || '',
        userType: 'Manager',
        userSubType: manager.userSubType || '',
        role: 'Manager',
        read: false,
        pendingTimestamp: new Date().toISOString(),
        timestamp: new Date().toISOString(),
        status: 'pending',
        type: NOTIFICATION_TYPES.ORGMANAGER_REQUEST,
        title: 'Manager Request',
        message: `${userData?.fullName || 'A vendor'} (${userData?.userSubType}) wants to add you as their organization manager. Accept to join their organization.`,
        createdBy: {
          userId: userData?.userId || null,
          fullName: userData?.fullName || '',
          userName: userData?.userName || '',
          email: userData?.email || '',
          profilePictureUrl: userData?.profilePictureUrl || '',
          userType: userData?.userType || '',
          userSubType: userData?.userSubType || ''
        }
      };
      
      // Create the notification
      await createNotification(manager.id, 'general', invitationData);
      
      // Clear search
      setManagerSearchQuery('');
      setManagerResults([]);
      
      // Close modal
      setIsManagerModalVisible(false);
      
    } catch (error) {
      console.error('Error adding manager:', error);
      alert('Failed to add manager. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveManager = async (managerId) => {
    if (!window.confirm('Are you sure you want to remove this manager?')) return;
    
    setIsProcessing(true);
    try {
      const db = getFirestoreInstance();
      
      // Find manager to get userId for notification
      const managerToRemove = managers.find(m => m.id === managerId);
      if (!managerToRemove) {
        throw new Error('Manager not found');
      }
      
      // Remove from organization's organizationManagers subcollection
      const managerRef = doc(db, 'users', userData.userId, 'organizationManagers', managerId);
      await deleteDoc(managerRef);
      
      // Update both state arrays
      setManagers(prev => prev.filter(m => m.id !== managerId));
      setDisplayedManagers(prev => prev.filter(m => m.id !== managerId));
      
      // Decrement the manager count
      const newManagerCount = Math.max(0, managerCount - 1);
      setManagerCount(newManagerCount);
      
      // Get new top 3 managers for main document
      const managersColRef = collection(db, 'users', userData.userId, 'organizationManagers');
      const top3Query = query(
        managersColRef,
        orderBy('createdAt', 'desc'),
        limit(3)
      );
      
      const top3Snapshot = await getDocs(top3Query);
      const top3Managers = await Promise.all(
        top3Snapshot.docs.map(async (docSnapshot) => {
          const managerData = docSnapshot.data();
          
          // Enrich with user data
          if (managerData.userId) {
            try {
              const userDoc = await getDoc(doc(db, 'users', managerData.userId));
              if (userDoc.exists()) {
                const userData = userDoc.data();
                return {
                  id: docSnapshot.id,
                  userId: managerData.userId,
                  fullName: userData.fullName || '',
                  userName: userData.userName || '',
                  profilePictureUrl: userData.profilePictureUrl || '',
                  userSubType: userData.userSubType || '',
                  status: managerData.status || 'pending',
                  role: managerData.role || 'manager',
                  createdAt: managerData.createdAt
                };
              }
            } catch (error) {
              console.error(`Error fetching user data for ${managerData.userId}:`, error);
            }
          }
          
          return {
            id: docSnapshot.id,
            ...managerData
          };
        })
      );
      
      // Update main document with top3 and the count
      const userRef = doc(db, 'users', userData.userId);
      await updateDoc(userRef, {
        organizationManagers: top3Managers,
        organizationManagersCount: newManagerCount
      });
      
      // Update hasMoreManagers based on the new count
      setHasMoreManagers(newManagerCount > top3Managers.length);
      
      // If the manager has already accepted, update their organization subcollection and send notification
      if (managerToRemove.status === 'accepted') {
        try {
          const orgRef = doc(db, 'users', managerToRemove.userId, 'organizations', userData.userId);
          const orgDoc = await getDoc(orgRef);
          
          if (orgDoc.exists()) {
            await updateDoc(orgRef, {
              status: 'removed',
              removedAt: new Date().toISOString()
            });
          }
        } catch (err) {
          console.error('Error updating manager organization entry:', err);
          // Continue even if this fails
        }
        
        // Only create removal notification if the manager had accepted
        const notificationData = {
          organizationId: userData?.userId || null,
          organizationName: userData?.fullName || '',
          type: NOTIFICATION_TYPES.ORGMANAGER_REMOVAL,
          title: 'Manager Removal',
          message: `${userData?.fullName || 'A vendor'} has removed you as manager from their organization.`,
          status: 'removed',
          timestamp: new Date().toISOString(),
          createdBy: {
            userId: userData?.userId || null,
            fullName: userData?.fullName || '',
            userSubType: userData?.userSubType || ''
          }
        };
        
        await createNotification(managerToRemove.userId, 'general', notificationData);
      } else {
        // For pending requests, find and delete the pending notification
        try {
          const db = getFirestoreInstance();
          const notificationsQuery = query(
            collection(db, 'users', managerToRemove.userId, 'notifications'),
            where('organizationId', '==', userData.userId),
            where('type', '==', NOTIFICATION_TYPES.ORGMANAGER_REQUEST),
            where('status', '==', 'pending')
          );
          
          const notificationsSnapshot = await getDocs(notificationsQuery);
          
          if (!notificationsSnapshot.empty) {
            // Delete all matching pending notifications
            const batch = writeBatch(db);
            notificationsSnapshot.docs.forEach(doc => {
              batch.delete(doc.ref);
            });
            await batch.commit();
          }
        } catch (error) {
          console.error('Error deleting pending manager request notifications:', error);
          // Continue even if this fails
        }
      }
      
    } catch (error) {
      console.error('Error removing manager:', error);
      alert('Failed to remove manager. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle tour selection (for RightMenu)
  const handleTourSelect = (tour) => {
    // Navigate to tour or update state as needed
  };

  // Manager Search Modal Component
  const ManagerSearchModal = () => {
    if (!isManagerModalVisible) return null;
    
    return (
      <div className={styles.modalOverlay} onClick={() => setIsManagerModalVisible(false)}>
        <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <h2>Add Manager</h2>
            <button className={styles.closeButton} onClick={() => setIsManagerModalVisible(false)}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className={styles.searchBarContainer}>
            <input
              type="text"
              placeholder="Search managers by name..."
              value={managerSearchQuery}
              onChange={(e) => setManagerSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
            <button
              className={styles.searchButton}
              onClick={handleManagerSearch}
              disabled={!managerSearchQuery.trim() || searchingManager}
            >
              {searchingManager ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
              <span>{searchingManager ? 'Searching...' : 'Search'}</span>
            </button>
          </div>
          
          <div className={styles.searchResults}>
            {searchingManager ? (
              <div className={styles.noResults}>Searching...</div>
            ) : managerResults.length > 0 ? (
              managerResults.map((manager) => (
                <div
                  key={manager.id}
                  className={`${styles.searchResult} ${manager.isAlreadyAdded ? styles.disabled : ''}`}
                  onClick={() => !manager.isAlreadyAdded && handleAddManager(manager)}
                >
                  <img
                    src={manager.profilePictureUrl || defaultUserImage}
                    alt={manager.fullName}
                    className={styles.profilePhoto}
                  />
                  <div className={styles.userInfo}>
                    <div className={styles.nameContainer}>
                      <span className={styles.fullName}>{manager.fullName}</span>
                      {manager.idVerificationStatus === 'Verified' && (
                        <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                      )}
                    </div>
                    <span className={styles.userName}>@{manager.userName}</span>
                    <span className={styles.userSubType}>{manager.userSubType}</span>
                  </div>
                  {manager.isAlreadyAdded && (
                    <span className={styles.alreadyAddedText}>Already Added</span>
                  )}
                </div>
              ))
            ) : managerSearchQuery && !searchingManager ? (
              <div className={styles.noResults}>No managers found</div>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.managersContainer}>
    <div className={styles['main-layout']}>
      {!isMobile && <LeftMenu />}
      <main className={styles['managers-main']}>
      <AuthCheckWrapper>
      {hasAccess ? (
          <>
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>Manager Management</h1>
          <p className={styles.pageDescription}>
            Add and manage organization managers for your events and operations.
          </p>
        </div>
        
        <div className={styles.managerActionsSection}>
          <div className={styles.searchContainer}>
            <button 
              className={styles.addManagerButton}
              onClick={() => setIsManagerModalVisible(true)}
            >
              <FontAwesomeIcon icon={faUserPlus} />
              <span>Add Manager</span>
            </button>
          </div>
        </div>
        
        <div className={styles.managerListContainer}>
          <h2 className={styles.sectionTitle}>Your Managers</h2>
          
          {displayedManagers.length === 0 ? (
            <div className={styles.emptyState}>
              <FontAwesomeIcon icon={faUsers} className={styles.emptyIcon} />
              <p>You haven't added any managers yet</p>
            </div>
          ) : (
            <>
              <div className={styles.managerList}>
                {displayedManagers.map((manager) => (
                  <div 
                    key={manager.id} 
                    className={styles.managerCard}
                  >
                    <div className={styles.managerCardContent}>
                      <div 
                        className={styles.managerInfo}
                        onClick={() => navigateToUserProfile(manager.userName)}
                        style={{ cursor: 'pointer' }}
                      >
                        <img
                          src={manager.profilePictureUrl || defaultUserImage}
                          alt={manager.fullName}
                          className={styles.managerImage}
                        />
                        <div className={styles.managerInfoText}>
                          <div className={styles.managerNameContainer}>
                            <span className={styles.managerName}>{manager.fullName}</span>
                            {manager.idVerificationStatus === 'Verified' && (
                              <img src={verifiedBadge} alt="Verified" className={styles.verifiedBadge} />
                            )}
                          </div>
                          <span className={styles.managerUsername}>@{manager.userName}</span>
                          <span className={styles.managerType}>{manager.userSubType}</span>
                        </div>
                      </div>
                        
                      <div className={styles.managerStatus}>
                        {manager.status === 'pending' && (
                          <span className={styles.pendingStatus}>Pending</span>
                        )}
                        {manager.status === 'accepted' && (
                          <span className={styles.acceptedStatus}>Active</span>
                        )}
                      </div>
                        
                      <div className={styles.managerActions}>
                        <button
                          className={styles.removeButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveManager(manager.id);
                          }}
                          title="Remove manager"
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Load more button */}
              {hasMoreManagers && (
                <button 
                  className={styles.loadMoreButton}
                  onClick={loadMoreManagers}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </button>
              )}
            </>
          )}
        </div>
          
        {/* Modals */}
        {ManagerSearchModal()}
          
        {/* Loading Overlay */}
        {isProcessing && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner}></div>
            <div className={styles.loadingText}>
              Processing...
            </div>
          </div>
        )}
        </>
        ) : (
          <PermissionDenied />
        )}
        </AuthCheckWrapper>
      </main>
      {!isMobile && <RightMenu tours={tours} onTourSelect={handleTourSelect} />}
    </div>
  </div>
  );
};

export default OrganizationManagers;